import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useLocale, useTranslations } from "next-intl";
import { intlLocaleOf } from "@/lib/i18n/intlLocale";
import { IconAlertTriangle, IconCheck, IconEye, IconEyeOff, IconPlus, IconX } from "@tabler/icons-react";
import { CheckboxWithValidation } from "@/components/Inputs/CheckboxWithValidation";
import { SMS_GUEST_FIELD_NAMES, useSmsChannelEnabled } from "@/lib/sms/smsCapabilities";
import { FormInput } from "@/components/Inputs/FormInput";
import { PillToggle } from "@/components/PillToggle/PillToggle";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import {
  groupFieldOptions,
  guestFieldGroupOfKey,
} from "@/lib/utils/field-groups";
import {
  ALLOWLISTABLE_GUEST_FIELDS,
  isImpliedAllowlistToken,
} from "@/lib/authz/fieldAllowlist";
import {
  ACCESS_DATA_KEYS,
  exceedsCap,
  fieldScopeReducer,
  groupForEditor,
  initialFieldScope,
  needsSensitiveConfirm,
  withheldByRevealRule,
  withoutSmsEntriesUnless,
  type AllowlistMode,
  type CatalogueEntry,
  type FieldScopeAction,
  type FieldScopeState,
  type HiddenRow,
  type VisibleRow,
} from "@/lib/authz/fieldExclusion";
import {
  CAPABILITY_CATALOGUE,
  type ActionBundle,
  type Capability,
} from "@/types/Capability";
import { useFieldScopeConfirm } from "./useFieldScopeConfirm";

/**
 * Dual-pane field picker. Left = available; right = allowed. Click an entry
 * to move it between panes. "No restriction" toggle clears the allowlist
 * entirely (sends `null` on save, meaning "all fields").
 *
 * The built-in candidates come from the live guest-family catalogue
 * ({@link ALLOWLISTABLE_GUEST_FIELDS}) — every column the lists can show is
 * grantable here. Action columns are deliberately NOT candidates: they carry
 * no data and follow the capability instead (see `ALLOWLIST_EXEMPT_FIELDS`).
 *
 * `actionBundle` is the one bridge across that line: a pinned "Aktion" entry
 * that MIRRORS the lists' "Aktion" column. It stands for the capabilities
 * behind that column AND the fields their actions read (`ACTION_BUNDLE`), so
 * one click makes the column appear and work — the admin looks for "Aktion"
 * among the fields, and finds it here whatever rights the role has. It is
 * DERIVED and never stored, and it is deliberately **add-only**:
 *
 *  - while a right or a field is missing it is a sticky row in the AVAILABLE
 *    pane naming exactly what the next click turns on (56 candidates in a
 *    `max-h-64` box — an offer that scrolls away is an offer nobody finds);
 *  - once every part is there it becomes an inert status line in the allowed
 *    pane, OUTSIDE the list: it is not a grantable column, so it must not look
 *    like one, and it must not inflate the pane's field count.
 *
 * No revoke: each field keeps its own X, each right its checkbox in the grid.
 * An umbrella X would have taken away, in one unconfirmed click, a column or a
 * right the admin had granted for a different reason. Unticking a right in the
 * grid turns the status back into an offer — the column just vanished from the
 * list, and the entry says so.
 *
 * "Add all fields (N)" (W1c-7, Figma HIGH 14456:50723 "all fields except…"):
 * one click grants every candidate the available pane lists RIGHT NOW — the
 * search narrows it and the label says "matching" — so "all except e-mail" is
 * add all, then the X on Email. It counts what the next click grants, stays
 * (disabled) at zero so keyboard focus has somewhere to live after the list
 * empties, and is not offered read-only.
 *
 * The host owns the capabilities, so it does the write (`onGrantAction`); the
 * pane names what changes. Mind the second edge: the stored list is ALSO the
 * write allowlist server-side (`AuthzWriteValidator.assertFixedFieldsAllowed`
 * ← `GuestResource.checkUpdateFieldAllowlist`), so granting `acceptanceState`
 * here also authorises a `guests:write` role to WRITE the accept/decline
 * decision. That is why the row names its rights and fields instead of just
 * saying "Aktion".
 *
 * B2 "All fields except …" (plan Rev. 5.2, FE-2). Below the unticked "All
 * fields" checkbox a named radiogroup picks the mode — "Only these" (the view
 * above, unchanged) or "All except": two groups, Visible (every list key,
 * with a marker where it is deleted, outside the catalogue, a stray token or
 * reveals a hidden name) and Hidden (the rest of the catalogue, tagged
 * "reveals …" › access data / sensitive › new), the keys the list reveals as
 * "visible via …", a counter "N of M visible" and "Show all new (normal
 * fields)". Every path into Visible that carries a SENSITIVE / ART9 /
 * unknown-level field or access data asks first. The stored list stays the
 * only authz truth; "All except" is metadata the host saves with it.
 *
 * Every edit is an action of the ONE reducer (`fieldScopeReducer`): a host
 * that owns the scope state passes `onScopeAction` (session memory survives);
 * without it the pane derives the state from `value`/`mode`/`exclusions` and
 * reports through `onChange(list)` (+ `onModeChange`, `onExclusionsChange`) —
 * the `onChange(list)` contract of the "Only these" view is unchanged.
 *
 * `fieldsLocked` locks the field rows, the mode and "All fields" but keeps the
 * "Aktion" offer (the preview bar on an "All except" role); `readOnly` locks
 * everything. While the guest field catalogue is loading or failed, "All
 * except" is read-only with a retry and the mode switch is off — never an
 * empty catalogue that would mark every granted custom field as deleted.
 */
/** Stable empty defaults — they feed memos, so a fresh array per render churns them. */
const NO_CAPS: readonly Capability[] = [];
const NO_FIELDS: readonly string[] = [];
const NO_KEYS: string[] = [];
const ACCESS_DATA: ReadonlySet<string> = new Set(ACCESS_DATA_KEYS);

export type FieldCatalogueStatus = "ready" | "loading" | "failed";

const MODES: AllowlistMode[] = ["include", "exclude"];

export function FieldAllowlistDualPane({
  value,
  onChange,
  customFields,
  actionBundle,
  missingActionCaps = NO_CAPS,
  onGrantAction,
  readOnly = false,
  mode = "include",
  exclusions = NO_KEYS,
  onModeChange,
  onExclusionsChange,
  onScopeAction,
  modeSwitch = false,
  fieldsLocked = false,
  lockedNote,
  catalogue = null,
  catalogueStatus,
  onRetryCatalogue,
  catalogueRetrying = false,
  onSensitiveConfirmed,
  elevatedConfirm = false,
}: {
  value: string[] | null;
  onChange: (next: string[] | null) => void;
  /** Custom field names + resolved display labels from the event's field schema. */
  customFields: { name: string; label: string }[];
  /**
   * What the "Aktion" entry stands for: the capabilities behind the lists'
   * action columns and the fields those actions read (`ACTION_BUNDLE`). Absent
   * ⇒ no entry. The entry mirrors the lists' "Aktion" column: it is offered
   * while ANY part is missing and reads as granted once every part is there.
   */
  actionBundle?: Readonly<ActionBundle>;
  /** The bundle's capabilities the role does not carry yet — the host owns the caps state. */
  missingActionCaps?: readonly Capability[];
  /**
   * One click turns on the whole bundle — capabilities AND fields. The host
   * owns both states, so it does the write; the pane only names what changes
   * (and, in "All except", asks first when a field needs it).
   */
  onGrantAction?: () => void;
  /**
   * Read-only editor (archived, plan-locked, no write right): the editor hides
   * the twin repair (its warning CTA) behind the same flag, and Save is
   * disabled — so the offer must not be the back door that dirties a form
   * nobody can save; "No restriction" and the field rows are off too (the
   * lists still show the role's fields).
   */
  readOnly?: boolean;
  /** B2: "Only these" (default) or "All except". Ignored while `value` is null. */
  mode?: AllowlistMode;
  /** B2: the keys the admin hid ("All except" only). */
  exclusions?: readonly string[];
  /** B2, derived-state hosts: the mode after an edit. */
  onModeChange?: (mode: AllowlistMode) => void;
  /** B2, derived-state hosts: the exclusions after an edit. */
  onExclusionsChange?: (exclusions: string[]) => void;
  /** B2: the host's reducer dispatch — every edit goes there instead of `onChange`. */
  onScopeAction?: (action: FieldScopeAction) => void;
  /** B2: render the "Only these / All except" radiogroup (the role editor; never the preview bar). */
  modeSwitch?: boolean;
  /** B2: lock the field rows, the mode and "All fields"; the "Aktion" offer stays. */
  fieldsLocked?: boolean;
  /** B2: shown above a locked pane (what to do instead). */
  lockedNote?: ReactNode;
  /** B2: the loud guest field catalogue (unfiltered — the pane applies the SMS predicate); null = unknown. */
  catalogue?: readonly CatalogueEntry[] | null;
  /** B2: absent = "ready" when a catalogue is given, else "failed". */
  catalogueStatus?: FieldCatalogueStatus;
  onRetryCatalogue?: () => void;
  /** B2 F1 (U-LOW-4): a catalogue retry is out — the retry button shows it busy. */
  catalogueRetrying?: boolean;
  /**
   * B2 F1 (S-MED-1): the keys the admin let into Visible through the
   * sensitive question — the host's save re-checks every key added in the
   * session that is sensitive NOW and was not confirmed.
   */
  onSensitiveConfirmed?: (keys: string[]) => void;
  /** Raise the pane's questions above a host modal (the preview bar). */
  elevatedConfirm?: boolean;
}) {
  const t = useTranslations("roleTemplates");
  const tRoot = useTranslations();
  const locale = useLocale();
  const tFields = useTranslations("guest-fields");
  const tGroups = useTranslations("fieldGroups");
  const [search, setSearch] = useState("");
  const { ask, dialog } = useFieldScopeConfirm({ elevated: elevatedConfirm });

  const noRestriction = value === null;
  const exclude = !noRestriction && mode === "exclude";
  const status: FieldCatalogueStatus = catalogueStatus ?? (catalogue ? "ready" : "failed");
  const catalogueReady = status === "ready" && !!catalogue;
  // "All except" without a catalogue cannot say what is hidden, sensitive or
  // new: read-only until it is there (plan FE-1 "loud catalogue").
  const rowsLocked = readOnly || fieldsLocked || (exclude && !catalogueReady);

  const catalogueLabelByKey = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of catalogue ?? []) if (e.label) m.set(e.key, e.label);
    return m;
  }, [catalogue]);
  const customLabelByKey = useMemo(() => {
    const m: Record<string, string> = {};
    for (const f of customFields) m[`additionalFields.${f.name}`] = f.label;
    return m;
  }, [customFields]);

  const labelFor = useCallback(
    (k: string): string => {
      if (k.startsWith("additionalFields.")) {
        return customLabelByKey[k] || catalogueLabelByKey.get(k) || k.slice("additionalFields.".length);
      }
      // A key this bundle has no copy for (a newer build's column) reads as itself.
      return tFields.has(k) ? tFields(k) : k;
    },
    [customLabelByKey, catalogueLabelByKey, tFields],
  );

  // Audit r1 B4: the SMS consent columns are offered only while the SMS
  // channel is on (fail closed — the catalogue is static, the channel is not);
  // a key the template already holds stays in the right pane, removable.
  const smsChannelEnabled = useSmsChannelEnabled();
  const smsCatalogue = useMemo(
    () => (catalogue ? withoutSmsEntriesUnless(catalogue, smsChannelEnabled) : null),
    [catalogue, smsChannelEnabled],
  );
  const availableFields = useMemo(() => {
    const allow = new Set(value ?? []);
    const candidates = [
      ...ALLOWLISTABLE_GUEST_FIELDS.filter((k) => smsChannelEnabled || !SMS_GUEST_FIELD_NAMES.has(k)),
      ...customFields.map((f) => `additionalFields.${f.name}`),
    ];
    const q = search.trim().toLowerCase();
    return candidates
      .filter((k) => !allow.has(k))
      .filter(
        (k) =>
          !q ||
          k.toLowerCase().includes(q) ||
          labelFor(k).toLowerCase().includes(q),
      );
  }, [value, customFields, search, labelFor, smsChannelEnabled]);

  // COLUMNS only: the projection tokens a composite column carries on the wire
  // (`seating` → `seatInfo`) are plumbing, not a second field — see
  // `ALLOWLIST_KEY_IMPLIES`. They are emitted with their column below and never
  // listed or counted here.
  const allowed = useMemo(
    () => (value ?? []).filter((k) => !isImpliedAllowlistToken(k)),
    [value],
  );

  // ── the one reducer ────────────────────────────────────────────────────
  // The state the pane renders and, for a derived-state host, edits.
  const derived: FieldScopeState = useMemo(
    () =>
      initialFieldScope({
        fieldAllowlist: value,
        fieldAllowlistMode: mode === "exclude" ? "exclude" : null,
        fieldExclusions: [...exclusions],
      }),
    [value, mode, exclusions],
  );
  const dispatch = (action: FieldScopeAction) => {
    if (onScopeAction) {
      onScopeAction(action);
      return;
    }
    const next = fieldScopeReducer(derived, action);
    if (next === derived) return;
    if (next.mode !== derived.mode) onModeChange?.(next.mode);
    if (onExclusionsChange && JSON.stringify(next.exclusions) !== JSON.stringify(derived.exclusions)) {
      onExclusionsChange(next.exclusions);
    }
    if (next.list !== derived.list) onChange(next.list);
  };
  /**
   * Into Visible. "All except" asks for the sensitive / access-data keys
   * first (the catalogue decides; unknown = sensitive); "Only these" is
   * today's behaviour. No question ⇒ synchronous. Resolves whether it ran.
   */
  const intoVisible = (keys: string[], run: () => void): Promise<boolean> => {
    const needs = exclude ? needsSensitiveConfirm(keys, catalogue ?? []) : NO_KEYS;
    if (needs.length === 0) {
      run();
      return Promise.resolve(true);
    }
    return ask("sensitive", needs.map(labelFor)).then((ok) => {
      if (!ok) return false;
      onSensitiveConfirmed?.(needs);
      run();
      return true;
    });
  };

  // The "Aktion" entry: granted ⇔ every capability behind the action columns
  // is ticked AND every field their actions read is on the list. Ticking a
  // right or a field by hand therefore moves the entry across on its own, and
  // an untick moves it back — there is no second piece of state to drift, and
  // the entry always agrees with what the lists' "Aktion" column does.
  const actionLabel = tFields("checkInOutCTA");
  const actionFields = actionBundle?.fields ?? NO_FIELDS;
  const actionCaps = actionBundle?.caps ?? NO_CAPS;
  const missingActionFields = useMemo(() => {
    const allow = new Set(value ?? []);
    return actionFields.filter((f) => !allow.has(f));
  }, [actionFields, value]);
  const hasActionEntry = actionCaps.length > 0 || actionFields.length > 0;
  const actionGranted =
    hasActionEntry &&
    missingActionCaps.length === 0 &&
    missingActionFields.length === 0;
  const capLabelFor = useCallback(
    (cap: Capability): string =>
      tRoot(CAPABILITY_CATALOGUE.find((d) => d.key === cap)?.i18nKey ?? cap, {
        defaultValue: cap,
      }),
    [tRoot],
  );
  // Locale-correct joining, not `join(", ")`: the locale set includes Welsh,
  // Irish and Maltese, and this repo already ruled the bare join out once
  // (`WorkflowWarningNotes`).
  const list = useCallback(
    (parts: string[]) =>
      new Intl.ListFormat(intlLocaleOf(locale), { style: "long", type: "conjunction" }).format(
        parts,
      ),
    [locale],
  );
  // The offer names what the NEXT click turns on — rights first, then fields —
  // and never a part already in place, which is what an admin reads as "it
  // didn't take". It is the promise the user asked for: "these get activated".
  const actionOfferSubLabel = useMemo(
    () =>
      t("actionEntryActivates", {
        items: list([
          ...missingActionCaps.map(capLabelFor),
          ...missingActionFields.map(labelFor),
        ]),
      }),
    [t, list, missingActionCaps, capLabelFor, missingActionFields, labelFor],
  );
  // The status line names the whole bundle: it answers "does the column work".
  const actionGrantedSubLabel = useMemo(
    () => list([...actionCaps.map(capLabelFor), ...actionFields.map(labelFor)]),
    [actionCaps, capLabelFor, actionFields, labelFor, list],
  );
  // Searchable by its own label AND by what it carries (label or raw key), so
  // "checked in" or "check-in" finds the one-click offer too, not just a column.
  const actionMatchesSearch = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      actionLabel.toLowerCase().includes(q) ||
      actionOfferSubLabel.toLowerCase().includes(q) ||
      actionFields.some((f) => f.toLowerCase().includes(q)) ||
      actionCaps.some((c) => c.toLowerCase().includes(q))
    );
  }, [search, actionLabel, actionOfferSubLabel, actionFields, actionCaps]);
  // `fieldsLocked` keeps the offer (caps stay editable in the preview bar);
  // "All except" without a catalogue cannot ask its question, so no offer.
  const showActionOffer =
    hasActionEntry && !actionGranted && !readOnly && !(exclude && !catalogueReady);

  // Semantic sections for BOTH panes (render-only — the allowlist itself is
  // set-semantic: FieldsProvider consumes it as a Set, previewStore sorts
  // before hashing, the backend intersects; the emitted `value` array keeps
  // its append order). Keys are already in the getFieldKey convention, so
  // guestFieldGroupOfKey routes them directly; label-sorted inside each group.
  // The left pane groups AFTER the search filter, so empty sections vanish.
  const availableGroups = useMemo(
    () =>
      groupFieldOptions(
        [...availableFields].sort((a, b) => labelFor(a).localeCompare(labelFor(b))),
        guestFieldGroupOfKey,
      ),
    [availableFields, labelFor],
  );
  const allowedGroups = useMemo(
    () =>
      groupFieldOptions(
        [...allowed].sort((a, b) => labelFor(a).localeCompare(labelFor(b))),
        guestFieldGroupOfKey,
      ),
    [allowed, labelFor],
  );

  // Both emit the canonical wire shape (through the reducer): a column's
  // projection tokens arrive with it and leave with it, and a stray token
  // never survives on its own in "Only these".
  const add = (k: string) => dispatch({ type: "show", keys: [k] });
  // Everything the available pane lists now (search applied) — the label counts
  // exactly that, and an empty list is a no-op, never an `onChange([])`-style
  // dirtying of the form.
  const addAllAvailable = () => {
    if (availableFields.length === 0) return;
    dispatch({ type: "show", keys: [...availableFields] });
  };
  const addAllLabel = search.trim()
    ? t("addAllMatching", { count: availableFields.length })
    : t("addAllFields", { count: availableFields.length });
  const remove = (k: string) => dispatch({ type: "hide", keys: [k] });
  // The bundle moves as one. The host owns the capabilities, so it does the
  // whole write when it can; without a host writer the pane still grants the
  // fields (`addList` keeps the first occurrence, so a field already granted
  // keeps its place, and a composite field's projection token rides along).
  // The click unmounts the offer (it turns into the status line), and a focus
  // that dies with its element lands on <body> — the keyboard user's next Tab
  // starts from the top of the page and nothing is announced. So: remember
  // that the grant came from here, and move focus onto the status once it
  // exists. `aria-live` on the status makes the change audible as well.
  const statusRef = useRef<HTMLParagraphElement | null>(null);
  const focusStatusRef = useRef(false);
  const grantAction = () => {
    void intoVisible(missingActionFields, () => {
      focusStatusRef.current = true;
      if (onGrantAction) {
        onGrantAction();
        return;
      }
      dispatch({ type: "addList", keys: [...actionFields] });
    });
  };
  useEffect(() => {
    if (!actionGranted || !focusStatusRef.current) return;
    focusStatusRef.current = false;
    statusRef.current?.focus();
  }, [actionGranted]);

  // ── B2: mode switch, cap, side effects, catalogue state ───────────────────
  const modeLocked = readOnly || fieldsLocked || !catalogueReady;
  const radioRefs = useRef<Record<AllowlistMode, HTMLButtonElement | null>>({ include: null, exclude: null });
  const pickMode = (next: AllowlistMode) => {
    if (modeLocked || next === mode || !smsCatalogue) return;
    dispatch({ type: "setMode", mode: next, catalogue: smsCatalogue });
  };
  const onModeKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    const step =
      e.key === "ArrowRight" || e.key === "ArrowDown"
        ? 1
        : e.key === "ArrowLeft" || e.key === "ArrowUp"
          ? -1
          : 0;
    if (step === 0 || modeLocked) return;
    e.preventDefault();
    const i = MODES.indexOf(mode);
    const next = MODES[(i + step + MODES.length) % MODES.length];
    pickMode(next);
    radioRefs.current[next]?.focus();
  };
  const overCap = exceedsCap(value, exclude ? "exclude" : "include");

  // "All except" says why it is read-only; "Only these" says why its mode
  // switch is off — loading (U-LOW-1), or a failed read with its retry.
  const baseId = useId();
  const modeHintId = `${baseId}-mode-hint`;
  const catalogueNoticeId = `${baseId}-catalogue-notice`;
  const catalogueNoticeShown =
    (exclude && status !== "ready") ||
    (!exclude && modeSwitch && (status === "loading" || (status === "failed" && !!onRetryCatalogue)));
  const catalogueNotice =
    catalogueNoticeShown ? (
      status === "loading" ? (
        <p id={catalogueNoticeId} role="status" className="body-3-regular font-tertiary" data-testid="field-scope-catalogue-loading">
          {t("fieldScope.catalogueLoading")}
        </p>
      ) : (
        <div
          role="alert"
          className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border outline-error px-3 py-2"
          data-testid="field-scope-catalogue-error"
        >
          <p id={catalogueNoticeId} className="flex-1 body-3-regular font-error">{t("fieldScope.catalogueFailed")}</p>
          {onRetryCatalogue && (
            <SecondaryButton
              text={t("retry")}
              size="small"
              width="w-max"
              loading={catalogueRetrying}
              disabled={catalogueRetrying}
              onClick={onRetryCatalogue}
              testId="field-scope-catalogue-retry"
            />
          )}
        </div>
      )
    ) : null;

  const actionOffer =
    showActionOffer && actionMatchesSearch ? (
      <li className="sticky top-0 z-10 surface-primary rounded-md">
        {/* A real button, not an `onClick` on the row: the field rows
            beside it are mouse-only (a pre-existing gap), but the offer
            is NEW and is the one-click route to "the actions work" —
            a keyboard user must be able to take it. */}
        <button
          type="button"
          onClick={grantAction}
          className="w-full text-left px-2 py-1 body-3-regular font-secondary hover:surface-secondary cursor-pointer flex items-start justify-between rounded-md gap-2 focus-ring"
        >
          {/* No `truncate`: the sub-label is the only place the granted
              fields are named, and it overflows 214px of pane in the
              longer locales. Let it wrap. */}
          <span>
            <span className="body-3-medium">{actionLabel}</span>
            <span className="font-tertiary"> · {actionOfferSubLabel}</span>
          </span>
          <IconPlus size={14} className="font-tertiary shrink-0 mt-0.5" aria-hidden />
        </button>
      </li>
    ) : null;

  const actionStatus = actionGranted ? (
    // Outside the list on purpose: a status, not a grantable column,
    // and never counted in the header above.
    <p
      ref={statusRef}
      tabIndex={-1}
      aria-live="polite"
      className="px-2 py-1 body-3-regular font-tertiary flex items-start gap-1.5 focus-ring rounded-md"
    >
      <IconCheck size={14} className="shrink-0 mt-0.5" aria-hidden />
      <span>
        <span className="body-3-medium font-secondary">{actionLabel}</span>
        {" · "}
        {actionGrantedSubLabel}
      </span>
    </p>
  ) : null;

  return (
    <div className="flex flex-col gap-3">
      {lockedNote && (
        <div data-testid="field-scope-locked">{lockedNote}</div>
      )}
      <CheckboxWithValidation
        isChecked={noRestriction}
        onCheckChangeAction={(v) => dispatch({ type: "setAllFields", on: v })}
        disabled={readOnly || fieldsLocked}
      >
        {t("noRestriction")}
      </CheckboxWithValidation>

      {!noRestriction && modeSwitch && (
        <div className="flex flex-col gap-1.5" data-testid="field-scope-mode">
          <p className="body-3-medium font-secondary" id="field-scope-mode-label">
            {t("fieldScope.modeLabel")}
          </p>
          <div
            role="radiogroup"
            aria-labelledby="field-scope-mode-label"
            // U-LOW-5: the hint (and, while the switch waits, why) belongs to the radios.
            aria-describedby={catalogueNoticeShown ? `${modeHintId} ${catalogueNoticeId}` : modeHintId}
            className="flex flex-wrap gap-2"
          >
            {MODES.map((m) => (
              <PillToggle
                key={m}
                role="radio"
                label={m === "exclude" ? t("fieldScope.modeExclude") : t("fieldScope.modeInclude")}
                active={mode === m}
                disabled={modeLocked}
                tabIndex={mode === m ? 0 : -1}
                onKeyDown={onModeKeyDown}
                buttonRef={(el) => {
                  radioRefs.current[m] = el;
                }}
                onClick={() => pickMode(m)}
                testId={`field-scope-mode-${m}`}
              />
            ))}
          </div>
          <p id={modeHintId} className="body-3-regular font-tertiary" data-testid="field-scope-mode-hint">
            {exclude ? t("fieldScope.modeExcludeHint") : t("fieldScope.modeIncludeHint")}
          </p>
        </div>
      )}

      {!noRestriction && catalogueNotice}

      {!noRestriction && overCap && (
        <p role="alert" className="body-3-regular font-error" data-testid="field-scope-cap">
          {t("fieldScope.capExceeded")}
        </p>
      )}

      {!noRestriction && !exclude && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="border outline-quaternary rounded-lg p-2 flex flex-col gap-2 min-h-[200px]">
            <FormInput
              id="field-allowlist-search"
              search
              placeholder={t("search")}
              value={search}
              onChangeAction={setSearch}
              margin=""
            />
            {!rowsLocked && (
              <div className="flex justify-end">
                {/* `aria-disabled`, not `disabled`: the button stays focusable
                    once the list is empty, so a keyboard user's focus does not
                    fall off the page the moment the last field moves right. */}
                <button
                  type="button"
                  onClick={addAllAvailable}
                  aria-disabled={availableFields.length === 0 || undefined}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border outline-quaternary body-3-medium focus-ring ${
                    availableFields.length === 0
                      ? "font-tertiary cursor-default"
                      : "font-secondary hover:surface-secondary cursor-pointer"
                  }`}
                >
                  <IconPlus size={14} className="shrink-0" aria-hidden />
                  {addAllLabel}
                </button>
              </div>
            )}
            <ul className="overflow-y-auto max-h-64 flex flex-col">
              {actionOffer}
              {availableGroups.map((group) => (
                <Fragment key={group.key}>
                  <li className="body-3-medium px-2 pt-2 pb-0.5 font-tertiary cursor-default">
                    {tGroups(group.key)}
                  </li>
                  {group.fields.map((k) => (
                    <li
                      key={k}
                      // Read-only: the lists show the role's fields; a row moves nothing.
                      onClick={rowsLocked ? undefined : () => add(k)}
                      aria-disabled={rowsLocked || undefined}
                      className={`px-2 py-1 body-3-regular font-secondary flex items-center justify-between rounded-md gap-2 ${
                        rowsLocked ? "cursor-default" : "hover:surface-secondary cursor-pointer"
                      }`}
                    >
                      <span className="truncate">{labelFor(k)}</span>
                      {!rowsLocked && <IconPlus size={14} className="font-tertiary shrink-0" />}
                    </li>
                  ))}
                </Fragment>
              ))}
            </ul>
          </div>

          <div className="border outline-quaternary rounded-lg p-2 flex flex-col gap-2 min-h-[200px]">
            <p className="body-3-regular font-tertiary px-2 py-1">
              {t("fieldAllowlist")} ({allowed.length})
            </p>
            {actionStatus}
            <ul className="overflow-y-auto max-h-64 flex flex-col">
              {allowedGroups.map((group) => (
                <Fragment key={group.key}>
                  <li className="body-3-medium px-2 pt-2 pb-0.5 font-tertiary cursor-default">
                    {tGroups(group.key)}
                  </li>
                  {group.fields.map((k) => (
                    <li
                      key={k}
                      onClick={rowsLocked ? undefined : () => remove(k)}
                      aria-disabled={rowsLocked || undefined}
                      className={`px-2 py-1 body-3-regular font-secondary flex items-center justify-between rounded-md gap-2 ${
                        rowsLocked ? "cursor-default" : "hover:surface-secondary cursor-pointer"
                      }`}
                    >
                      <span className="truncate">{labelFor(k)}</span>
                      {!rowsLocked && <IconX size={14} className="font-tertiary shrink-0" />}
                    </li>
                  ))}
                </Fragment>
              ))}
              {allowed.length === 0 && (
                <li className="body-3-regular font-tertiary px-2 py-1">
                  {t("fieldAllowlistEmpty")}
                </li>
              )}
            </ul>
          </div>
        </div>
      )}

      {exclude && (
        <ExcludeView
          state={derived}
          catalogue={catalogueReady ? smsCatalogue : null}
          labelFor={labelFor}
          listFormat={list}
          search={search}
          onSearch={setSearch}
          locked={rowsLocked}
          onShow={(keys) => intoVisible(keys, () => dispatch({ type: "show", keys }))}
          idPrefix={baseId}
          onShowAllNew={(keys) => {
            void ask("showAllNew", keys.map(labelFor)).then((ok) => {
              if (ok) dispatch({ type: "show", keys });
            });
          }}
          onHide={(key) => dispatch({ type: "hide", keys: [key] })}
          actionOffer={actionOffer}
          actionStatus={actionStatus}
        />
      )}

      {!noRestriction && (
        <p className="body-3-regular font-tertiary" data-testid="field-scope-side-effects">
          {t("fieldScope.sideEffects")}
        </p>
      )}
      {dialog}
    </div>
  );
}

type ExcludeList = "hidden" | "visible";

/**
 * "All except": Hidden (left — where "Only these" offers its candidates, and
 * where the "Aktion" offer sits) and Visible (right). Every row is a real
 * button (keyboard reachable); tags and markers are text, not colour only.
 *
 * B2 F1 (U-MED-1): a Show / Hide moves its row to the other list, and the
 * button it was pressed on goes with it — focus would fall to <body>. It
 * follows the field instead: onto its button in the other list, else the
 * next row of the list it left, else that list's title. A Show that asked
 * first does the same once the question is answered; a cancelled one leaves
 * focus where the question put it back (the Show button).
 */
function ExcludeView({
  state,
  catalogue,
  labelFor,
  listFormat,
  search,
  onSearch,
  locked,
  onShow,
  onShowAllNew,
  onHide,
  actionOffer,
  actionStatus,
  idPrefix,
}: {
  state: FieldScopeState;
  /** SMS-filtered; null while unknown (no orphan marking, no counter). */
  catalogue: readonly CatalogueEntry[] | null;
  labelFor: (k: string) => string;
  listFormat: (parts: string[]) => string;
  search: string;
  onSearch: (v: string) => void;
  locked: boolean;
  /** Resolves whether the show ran (false: the sensitive question was cancelled). */
  onShow: (keys: string[]) => Promise<boolean>;
  onShowAllNew: (keys: string[]) => void;
  onHide: (key: string) => void;
  actionOffer: ReactNode;
  actionStatus: ReactNode;
  idPrefix: string;
}) {
  const t = useTranslations("roleTemplates");
  const tGroups = useTranslations("fieldGroups");
  const groups = useMemo(() => groupForEditor(state, catalogue), [state, catalogue]);
  const titleId = (list: ExcludeList) => `${idPrefix}-${list}-title`;

  // ── focus follows the moved field (U-MED-1) ─────────────────────────────
  const buttons = useRef(new Map<string, HTMLButtonElement>());
  const buttonRef = (list: ExcludeList, key: string) => (el: HTMLButtonElement | null) => {
    if (el) buttons.current.set(`${list}:${key}`, el);
    else buttons.current.delete(`${list}:${key}`);
  };
  const titles = useRef<Record<ExcludeList, HTMLParagraphElement | null>>({ hidden: null, visible: null });
  const focusPlan = useRef<{ key: string; from: ExcludeList; to: ExcludeList; next: string | null } | null>(null);
  const planMove = (key: string, from: ExcludeList, order: readonly string[]) => {
    const i = order.indexOf(key);
    focusPlan.current = {
      key,
      from,
      to: from === "hidden" ? "visible" : "hidden",
      next: i === -1 ? null : (order[i + 1] ?? order[i - 1] ?? null),
    };
  };
  useEffect(() => {
    const plan = focusPlan.current;
    if (!plan) return;
    focusPlan.current = null;
    const at = (list: ExcludeList, key: string | null) => (key ? buttons.current.get(`${list}:${key}`) : undefined);
    // Not moved (an edit that changed nothing here): focus stays put.
    if (!at(plan.to, plan.key) && at(plan.from, plan.key)) return;
    (at(plan.to, plan.key) ?? at(plan.from, plan.next) ?? titles.current[plan.from])?.focus();
  }, [groups]);
  const q = search.trim().toLowerCase();
  const matches = useCallback(
    (k: string) => !q || k.toLowerCase().includes(q) || labelFor(k).toLowerCase().includes(q),
    [q, labelFor],
  );
  const byLabel = (a: string, b: string) => labelFor(a).localeCompare(labelFor(b));
  const hiddenByKey = useMemo(() => new Map(groups.hidden.map((h) => [h.key, h])), [groups.hidden]);
  const visibleByKey = useMemo(() => new Map(groups.visible.map((v) => [v.key, v])), [groups.visible]);
  const hiddenGroups = groupFieldOptions(
    groups.hidden.map((h) => h.key).filter(matches).sort(byLabel),
    guestFieldGroupOfKey,
  );
  const visibleGroups = groupFieldOptions(
    groups.visible.map((v) => v.key).sort(byLabel),
    guestFieldGroupOfKey,
  );
  // Display order — "the next row" is the one below on screen.
  const hiddenOrder = hiddenGroups.flatMap((g) => g.fields);
  const visibleOrder = visibleGroups.flatMap((g) => g.fields);
  const names = (keys: readonly string[]) => listFormat(keys.map(labelFor));

  const tagText = (h: HiddenRow): string | null => {
    if (h.tag === "revealsLastName") return t("fieldScope.tagReveals", { fields: names(h.reveals ?? []) });
    if (h.tag === "sensitive") return ACCESS_DATA.has(h.key) ? t("fieldScope.tagAccessData") : t("fieldScope.tagSensitive");
    if (h.tag === "new") return t("fieldScope.tagNew");
    return null;
  };
  const markerText = (v: VisibleRow): string | null => {
    if (v.marker === "revealsLastName") {
      const reveals = v.reveals ?? [];
      return t("fieldScope.markerReveals", { count: reveals.length, fields: names(reveals) });
    }
    if (v.marker === "deleted") return t("fieldScope.markerDeleted");
    if (v.marker === "stray") return t("fieldScope.markerStray");
    if (v.marker === "outsideCatalogue") return t("fieldScope.markerOutsideCatalogue");
    return null;
  };
  // A revealer the reveal rule would withhold cannot be shown on its own: its
  // Show is off with the reason (the row's tag names the keys at stake).
  const withheld = (key: string) => withheldByRevealRule(state, [key]).length > 0;

  return (
    <div className="flex flex-col gap-2" data-testid="field-scope-exclude">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {groups.counts && (
          <p className="body-3-medium font-secondary" data-testid="field-scope-counter">
            {t("fieldScope.counter", { visible: groups.counts.visible, total: groups.counts.total })}
          </p>
        )}
        {!locked && (
          <button
            type="button"
            onClick={() => groups.newNormal.length > 0 && onShowAllNew([...groups.newNormal])}
            aria-disabled={groups.newNormal.length === 0 || undefined}
            data-testid="field-scope-show-all-new"
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border outline-quaternary body-3-medium focus-ring ${
              groups.newNormal.length === 0
                ? "font-tertiary cursor-default"
                : "font-secondary hover:surface-secondary cursor-pointer"
            }`}
          >
            <IconEye size={14} className="shrink-0" aria-hidden />
            {t("fieldScope.showAllNew", { count: groups.newNormal.length })}
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="border outline-quaternary rounded-lg p-2 flex flex-col gap-2 min-h-[200px]" data-testid="field-scope-hidden">
          {/* U-LOW-5: the title names the list (and is focus's last resort, U-MED-1). */}
          <p
            id={titleId("hidden")}
            ref={(el) => {
              titles.current.hidden = el;
            }}
            tabIndex={-1}
            className="body-3-medium font-secondary px-2 py-1 rounded-md focus-ring"
          >
            {t("fieldScope.hiddenTitle")}
          </p>
          <FormInput
            id="field-allowlist-search"
            search
            placeholder={t("search")}
            value={search}
            onChangeAction={onSearch}
            margin=""
          />
          <ul aria-labelledby={titleId("hidden")} className="overflow-y-auto max-h-64 flex flex-col">
            {actionOffer}
            {hiddenGroups.map((group) => (
              <Fragment key={group.key}>
                <li className="body-3-medium px-2 pt-2 pb-0.5 font-tertiary cursor-default">{tGroups(group.key)}</li>
                {group.fields.map((k) => {
                  const row = hiddenByKey.get(k);
                  if (!row) return null;
                  const tag = tagText(row);
                  const blocked = row.tag === "revealsLastName" || withheld(k);
                  const reveals = row.reveals ?? [];
                  const reason = blocked
                    ? t("fieldScope.tagRevealsHint", { count: reveals.length, fields: names(reveals) })
                    : undefined;
                  const reasonId = `${idPrefix}-reason-${k}`;
                  return (
                    <li
                      key={k}
                      className="px-2 py-1 body-3-regular font-secondary flex items-center justify-between rounded-md gap-2"
                      data-testid={`field-scope-row-${k}`}
                      data-state="hidden"
                      data-tag={row.tag ?? ""}
                    >
                      <span className="min-w-0 flex flex-wrap items-center gap-1.5">
                        <span className="truncate">{labelFor(k)}</span>
                        {tag && (
                          <span
                            className={`px-1.5 py-0.5 rounded-md body-4-medium ${
                              row.tag === "new" ? "chip-blue-bg chip-blue-fg" : "chip-orange-bg chip-orange-fg"
                            }`}
                            data-testid="field-scope-tag"
                          >
                            {tag}
                          </span>
                        )}
                      </span>
                      {!locked && (
                        <button
                          type="button"
                          ref={buttonRef("hidden", k)}
                          onClick={() => {
                            if (blocked) return;
                            planMove(k, "hidden", hiddenOrder);
                            void onShow([k]).then((ran) => {
                              if (!ran && focusPlan.current?.key === k) focusPlan.current = null;
                            });
                          }}
                          aria-disabled={blocked || undefined}
                          aria-label={t("fieldScope.show", { field: labelFor(k) })}
                          // U-LOW-5: the reason is read with the button, not only hovered.
                          aria-describedby={blocked ? reasonId : undefined}
                          title={reason}
                          data-testid="field-scope-show"
                          className={`shrink-0 p-1 rounded-md focus-ring ${blocked ? "font-quaternary cursor-default" : "font-tertiary hover:surface-secondary cursor-pointer"}`}
                        >
                          <IconEye size={14} aria-hidden />
                        </button>
                      )}
                      {!locked && blocked && (
                        <span id={reasonId} className="sr-only" data-testid="field-scope-show-reason">
                          {reason}
                        </span>
                      )}
                    </li>
                  );
                })}
              </Fragment>
            ))}
            {groups.hidden.length === 0 && (
              <li className="body-3-regular font-tertiary px-2 py-1">{t("fieldScope.emptyHidden")}</li>
            )}
          </ul>
        </div>

        <div className="border outline-quaternary rounded-lg p-2 flex flex-col gap-2 min-h-[200px]" data-testid="field-scope-visible">
          <p
            id={titleId("visible")}
            ref={(el) => {
              titles.current.visible = el;
            }}
            tabIndex={-1}
            className="body-3-medium font-secondary px-2 py-1 rounded-md focus-ring"
          >
            {t("fieldScope.visibleTitle")}
          </p>
          {actionStatus}
          <ul aria-labelledby={titleId("visible")} className="overflow-y-auto max-h-64 flex flex-col">
            {visibleGroups.map((group) => (
              <Fragment key={group.key}>
                <li className="body-3-medium px-2 pt-2 pb-0.5 font-tertiary cursor-default">{tGroups(group.key)}</li>
                {group.fields.map((k) => {
                  const row = visibleByKey.get(k);
                  if (!row) return null;
                  const marker = markerText(row);
                  return (
                    <li
                      key={k}
                      className="px-2 py-1 body-3-regular font-secondary flex items-center justify-between rounded-md gap-2"
                      data-testid={`field-scope-row-${k}`}
                      data-state="visible"
                      data-marker={row.marker ?? ""}
                    >
                      <span className="min-w-0 flex flex-wrap items-center gap-1.5">
                        <span className="truncate">{labelFor(k)}</span>
                        {marker && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md body-4-medium chip-yellow-bg chip-yellow-fg" data-testid="field-scope-marker">
                            {row.marker === "revealsLastName" && <IconAlertTriangle size={12} aria-hidden />}
                            {marker}
                          </span>
                        )}
                      </span>
                      {!locked && (
                        <button
                          type="button"
                          ref={buttonRef("visible", k)}
                          onClick={() => {
                            planMove(k, "visible", visibleOrder);
                            onHide(k);
                          }}
                          aria-label={t("fieldScope.hide", { field: labelFor(k) })}
                          data-testid="field-scope-hide"
                          className="shrink-0 p-1 rounded-md font-tertiary hover:surface-secondary cursor-pointer focus-ring"
                        >
                          <IconEyeOff size={14} aria-hidden />
                        </button>
                      )}
                    </li>
                  );
                })}
              </Fragment>
            ))}
            {groups.revealedVia.map(({ key, via }) => (
              <li
                key={`via-${key}`}
                className="px-2 py-1 body-3-regular font-tertiary flex items-center gap-1.5 rounded-md"
                data-testid={`field-scope-via-${key}`}
              >
                <span className="truncate">{labelFor(key)}</span>
                <span className="body-4-medium">· {t("fieldScope.visibleVia", { fields: names(via) })}</span>
              </li>
            ))}
            {groups.visible.length === 0 && groups.revealedVia.length === 0 && (
              <li className="body-3-regular font-tertiary px-2 py-1">{t("fieldAllowlistEmpty")}</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
